import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";

// Cell Components with proper PropTypes
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="bold">
    {value}
  </MDTypography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

const MemberCell = ({ value, row }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    <MDAvatar name={value} size="sm" />
    <MDBox ml={2} lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {value}
      </MDTypography>
      <MDTypography variant="caption" color="text">
        {row.original.phone || "No phone"}
      </MDTypography>
    </MDBox>
  </MDBox>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      phone: PropTypes.string,
    }),
  }).isRequired,
};

const StatusCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "active" ? "success" : "error"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <MDBox display="flex" justifyContent="center" gap={1}>
    <MDButton variant="gradient" color="info" size="small" onClick={() => handleEdit(row.original)}>
      <Icon>edit</Icon>&nbsp;Edit
    </MDButton>
    <MDButton
      variant="gradient"
      color="error"
      size="small"
      onClick={() => handleDelete(row.original)}
    >
      <Icon>delete</Icon>&nbsp;Delete
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useUserData() {
  const { user, church } = useAuth();

  const [members, setMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [userData, setUserData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Member", accessor: "memberName", align: "left", width: "25%", Cell: MemberCell },
      { Header: "Username", accessor: "username", align: "center", width: "15%", Cell: SerialCell },
      { Header: "Email", accessor: "email", align: "center", width: "15%", Cell: SerialCell },
      { Header: "Role", accessor: "role", align: "center", width: "15%", Cell: SerialCell },
      { Header: "Status", accessor: "status", align: "center", width: "15%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const fetchMembers = useCallback(async () => {
    try {
      setIsLoadingMembers(true);
      const token = localStorage.getItem("authToken");
      if (!token || !church?.id) {
        setMembers([]);
        return;
      }

      const response = await fetch(`https://api.ofi-cia.com/api/members/church/${church.id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch members");

      const data = await response.json();
      setMembers(data || []);
    } catch (error) {
      console.error("Error fetching members:", error);
      Swal.fire("Error", "Failed to load members", "error");
      setMembers([]);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [church?.id]);

  const handleMemberSelect = useCallback((member) => {
    setSelectedUser((prev) => ({
      ...prev,
      member_id: member.id,
      member_name: `${member.first_name || ""} ${member.surname || ""}`.trim(),
      email: member.email || prev?.email || "",
      phone: member.phone || prev?.phone || "",
    }));
  }, []);

  const handleEdit = useCallback(
    (user) => {
      setSelectedUser({
        ...user,
        member_name: user.memberName,
        confirmPassword: "",
      });
      fetchMembers();
      setEditModalOpen(true);
    },
    [fetchMembers]
  );

  const handleDelete = useCallback((user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  }, []);

  const handleAddUser = useCallback(() => {
    setSelectedUser({
      id: null,
      member_id: null,
      member_name: "",
      username: "",
      name: "", // Added name field
      email: "",
      phone: "",
      role: "member",
      status: "active",
      password: "",
      confirmPassword: "",
    });
    fetchMembers();
    setEditModalOpen(true);
  }, [fetchMembers]);

  const fetchUsers = useCallback(async () => {
    try {
      setUserData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) {
        setUserData((prev) => ({ ...prev, isLoading: false, rows: [] }));
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        setUserData((prev) => ({ ...prev, isLoading: false, rows: [] }));
        return;
      }

      const response = await fetch(`https://api.ofi-cia.com/api/users/${church.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      console.log("API Response:", result); // Debug log

      const users = result.data || result.users || result || [];
      console.log("Processed users:", users);

      setUserData((prev) => ({
        ...prev,
        rows: users.map((user, index) => ({
          id: user.id,
          serial: index + 1,
          memberName:
            user.member?.first_name && user.member?.surname
              ? `${user.member.first_name} ${user.member.surname}`
              : user.member_name || "N/A",
          username: user.name || user.username || "N/A",
          email: user.email,
          role: user.role,
          status: user.status || "active",
          createdAt: user.created_at || new Date().toISOString(),
          originalData: user,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setUserData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
        rows: [],
      }));
    }
  }, [user, church]);

  const handleEditSubmit = useCallback(async () => {
    try {
      if (!selectedUser) return;

      // Validation
      if (!selectedUser.member_id) {
        throw new Error("Please select a member");
      }
      if (!selectedUser.username && !selectedUser.name) {
        throw new Error("Username/Name is required");
      }
      if (!selectedUser.email) {
        throw new Error("Email is required");
      }
      if (!selectedUser?.id && (!selectedUser.password || selectedUser.password.length < 6)) {
        throw new Error("Password must be at least 6 characters");
      }
      if (!selectedUser?.id && selectedUser.password !== selectedUser.confirmPassword) {
        throw new Error("Passwords do not match");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      if (!church?.id) {
        throw new Error("Church ID not found");
      }

      const url = selectedUser?.id
        ? `https://api.ofi-cia.com/api/users/${selectedUser.id}`
        : "https://api.ofi-cia.com/api/users";
      const method = selectedUser?.id ? "PUT" : "POST";

      // Check what field your backend expects - 'name' or 'username'
      const payload = {
        member_id: selectedUser.member_id,
        name: selectedUser.username || selectedUser.name, // Try both
        username: selectedUser.username || selectedUser.name, // Try both
        email: selectedUser.email,
        phone: selectedUser.phone || "",
        role: selectedUser.role,
        status: selectedUser.status,
        church_id: church.id,
      };

      // Remove undefined fields
      Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

      if (!selectedUser?.id) {
        payload.password = selectedUser.password;
      }

      console.log("Sending payload:", payload); // Debug log

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();
      console.log("API Response:", responseData); // Debug log

      if (!response.ok) {
        throw new Error(responseData.message || responseData.error || "Failed to save user");
      }

      Swal.fire({
        title: "Success!",
        text: `User ${selectedUser?.id ? "updated" : "created"} successfully`,
        icon: "success",
      });

      fetchUsers();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Submit failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  }, [selectedUser, church?.id, fetchUsers]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token || !selectedUser) {
        throw new Error("Authentication token or selected user not found");
      }

      const response = await fetch(`https://api.ofi-cia.com/api/users/${selectedUser.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();
      console.log("Delete response:", responseData); // Debug log

      if (!response.ok)
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: "User deleted successfully",
        icon: "success",
      });

      fetchUsers();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  }, [selectedUser, fetchUsers]);

  useEffect(() => {
    if (church?.id) {
      fetchUsers();
    }
  }, [church?.id, fetchUsers]);

  return {
    ...userData,
    members: members || [],
    isLoadingMembers,
    handleMemberSelect,
    handleEdit,
    handleDelete,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedUser,
    setSelectedUser,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddUser,
  };
}
